import {Injectable} from '@angular/core';

import {Observable, of} from 'rxjs';
import {SmagApiService} from '../api/smag-api.service';
import {catchError, first, map, switchMap} from 'rxjs/operators';
import {
    EnergyCommunityNetwork as SmagEnergyCommunityNetwork,
    EntityNetworkType as SmagEntityNetworkType,
    Permission
} from 'smag-commons/dto/api';
import {EnergyCommunityNetwork} from './entities';
import {HierarchyEntity, HierarchyService} from './hierarchy.service';
import {Entity} from 'smag-commons/cfg/hierarchy';
import {can} from './permissions';
import {MsgBoxService} from './msg-box.service';

/**
 * Service that deals with configuration of electrical/thermal networks.
 */
@Injectable()
export class NetworkService {

    constructor(private readonly apiService: SmagApiService,
                private readonly hierarchyService: HierarchyService,
                private readonly msgBoxService: MsgBoxService) {
    }

    buildECNetwork(id: string): Observable<EnergyCommunityNetwork> {
        return this.apiService.getNetwork(id).pipe(
            switchMap(network => network.type === SmagEntityNetworkType.ENERGY_COMMUNITY ?
                of(network as SmagEnergyCommunityNetwork) :
                of(null as SmagEnergyCommunityNetwork)),
            switchMap(ecNetwork => {
                return this.hierarchyService.hierarchyIds.pipe(first()).pipe(
                    switchMap(entityIds => this.buildEnergyCommunityEntityNetwork(ecNetwork, entityIds))
                );
            })
        );
    }

    buildECNetworkOfResource(entityId: string): Observable<EnergyCommunityNetwork> {
        return this.apiService.getNetworkOfResource(entityId).pipe(
            switchMap(networkInfo => networkInfo ?
                this.buildECNetwork(networkInfo.network_id) :
                of(null))
        );
    }

    private buildEnergyCommunityEntityNetwork(network: SmagEnergyCommunityNetwork,
                                              entityIds: { [entityId: string]: HierarchyEntity }): Observable<EnergyCommunityNetwork> {
        // (only) for network's root, full details are loaded
        return (can(entityIds[network.network_id].permissions, Permission.READ) ?
            this.loadNetworkEntity(network.network_id) :
            of(null as Entity)).pipe(
            map(communityEntity => {
                const entities: { [id: string]: HierarchyEntity } = {};
                const community = entityIds[network.network_id];
                entities[network.network_id] = community;
                Object.keys(network.pods).forEach(podK => {
                    entities[podK] = entityIds[podK];
                    Object.keys(network.pods[podK].plants || {}).forEach(plantKey => {
                        entities[plantKey] = entityIds[plantKey];
                        if (network.pods[plantKey]) {
                            Object.keys(network.pods[plantKey].storages).forEach(stoK => entities[stoK] = entityIds[stoK]);
                        }
                    });
                    Object.keys(network.pods[podK].storages || {}).forEach(stoK => entities[stoK] = entityIds[stoK]);
                });
                return {
                    root: community,
                    rootEntity: communityEntity,
                    entities,
                    pods: network.pods
                } as EnergyCommunityNetwork;
            })
        );
    }

    private loadNetworkEntity(networkId: string): Observable<Entity> {
        return this.apiService.getEntity(networkId).pipe(
            catchError(err => {
                this.msgBoxService.error(`An error occurred while loading network ${networkId}`, err, false);
                return of(null);
            })
        );
    }
}
